import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 700,
  },
  filterSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
  },
  statsCard: {
    textAlign: "center",
    marginBottom: theme.spacing(2),
    borderRadius: 8,
  },
  statsValue: {
    fontSize: "1.8rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  sectionTitle: {
    padding: "16px 16px 8px",
    fontWeight: 600,
  },
  laneCard: {
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.08)",
  },
  laneHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px 4px",
  },
  laneBadge: {
    display: "inline-block",
    borderRadius: 12,
    padding: "2px 10px",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#fff",
    marginRight: 8,
  },
  laneBar: {
    margin: "4px 16px 12px",
    height: 6,
    borderRadius: 3,
  },
  table: {
    minWidth: 650,
  },
  chip: {
    display: "inline-block",
    borderRadius: 10,
    padding: "2px 8px",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#fff",
  },
  card: {
    marginBottom: theme.spacing(2),
    borderRadius: 8,
  },
}));

const RelatorioVendas = ({ embedded = false }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [relatorio, setRelatorio] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd"),
    dateTo: format(new Date(), "yyyy-MM-dd"),
    userId: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.users);
    } catch (err) {
      toastError(err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerateReport = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      toast.error("Por favor, selecione o período");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      });
      if (filters.userId) params.append("userId", filters.userId);
      const url = `/ticketreport/vendas?${params}`;
      console.log("[PIPELINE-DEBUG-FE] Requisição:", url);
      const { data } = await api.get(url);
      console.log("[PIPELINE-DEBUG-FE] Resposta completa:", JSON.stringify(data));
      setRelatorio(data);
    } catch (err) {
      console.error("[PIPELINE-DEBUG-FE] ERRO:", err?.response?.status, err?.response?.data || err?.message);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);

  const ticketMedio =
    relatorio && relatorio.totalTickets > 0
      ? relatorio.totalValorPipeline / relatorio.totalTickets
      : 0;

  const maxLaneValor =
    relatorio && relatorio.porLane.length > 0
      ? Math.max(...relatorio.porLane.map((l) => l.totalValor))
      : 1;

  const filterSection = (
    <Paper className={classes.filterSection} elevation={0}>
      <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8 }}>
        <TextField
          label="Data Inicial"
          type="date"
          value={filters.dateFrom}
          onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
          style={{ width: 160 }}
        />
        <TextField
          label="Data Final"
          type="date"
          value={filters.dateTo}
          onChange={(e) => handleFilterChange("dateTo", e.target.value)}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
          style={{ width: 160 }}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Atendente</InputLabel>
          <Select
            value={filters.userId}
            onChange={(e) => handleFilterChange("userId", e.target.value)}
            label="Atendente"
          >
            <MenuItem value="">Todos</MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateReport}
          disabled={loading}
          size="small"
          style={{ height: 40, whiteSpace: "nowrap" }}
        >
          {loading ? "Gerando..." : "Gerar Relatório"}
        </Button>
      </Box>
    </Paper>
  );

  if (!relatorio) {
    return (
      <div className={classes.root}>
        {!embedded && (
          <Typography variant="h5" className={classes.title}>
            Pipeline de Vendas
          </Typography>
        )}
        {filterSection}
        {loading && <LinearProgress />}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {!embedded && (
        <Typography variant="h5" className={classes.title}>
          Pipeline de Vendas
        </Typography>
      )}

      {filterSection}

      {/* Cards de totais */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statsCard} elevation={2}>
            <CardContent>
              <Typography className={classes.statsValue}>
                {formatCurrency(relatorio.totalValorPipeline)}
              </Typography>
              <Typography className={classes.statsLabel}>
                Valor Total de Vendas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statsCard} elevation={2}>
            <CardContent>
              <Typography className={classes.statsValue}>
                {relatorio.totalTickets}
              </Typography>
              <Typography className={classes.statsLabel}>
                Vendas no Período
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statsCard} elevation={2}>
            <CardContent>
              <Typography className={classes.statsValue}>
                {formatCurrency(ticketMedio)}
              </Typography>
              <Typography className={classes.statsLabel}>
                Ticket Médio
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Funil por etapa */}
      {relatorio.porLane.length > 0 && (
        <Paper className={classes.card} elevation={1}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Funil de Vendas por Etapa
          </Typography>
          {relatorio.porLane.map((lane) => {
            const pct =
              maxLaneValor > 0 ? (lane.totalValor / maxLaneValor) * 100 : 0;
            return (
              <div key={lane.laneId}>
                <div className={classes.laneHeader}>
                  <Box display="flex" alignItems="center">
                    <span
                      className={classes.laneBadge}
                      style={{ backgroundColor: lane.laneColor }}
                    >
                      {lane.laneName}
                    </span>
                    <Typography variant="body2" color="textSecondary">
                      {lane.totalTickets} venda{lane.totalTickets !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                    {formatCurrency(lane.totalValor)}
                  </Typography>
                </div>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  className={classes.laneBar}
                  style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
                />
              </div>
            );
          })}
        </Paper>
      )}

      {/* Tabela de oportunidades */}
      <Paper className={classes.card} elevation={1}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Vendas ({relatorio.tickets.length})
        </Typography>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Etapa</TableCell>
              <TableCell>Atendente</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Última atualização</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {relatorio.tickets.map((ticket) => (
              <TableRow key={ticket.id}>
                <TableCell>{ticket.id}</TableCell>
                <TableCell>{ticket.contactName}</TableCell>
                <TableCell>
                  <span
                    className={classes.chip}
                    style={{ backgroundColor: ticket.laneColor }}
                  >
                    {ticket.lane}
                  </span>
                </TableCell>
                <TableCell>{ticket.userName}</TableCell>
                <TableCell align="right">
                  {formatCurrency(ticket.valor)}
                </TableCell>
                <TableCell>
                  {ticket.updatedAt
                    ? format(new Date(ticket.updatedAt), "dd/MM/yyyy HH:mm")
                    : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Tabela por atendente */}
      {relatorio.atendentes.length > 0 && (
        <Paper className={classes.card} elevation={1}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Desempenho por Atendente
          </Typography>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Atendente</TableCell>
                <TableCell align="right">Vendas</TableCell>
                <TableCell align="right">Valor Total</TableCell>
                <TableCell align="right">Ticket Médio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {relatorio.atendentes.map((atendente, idx) => (
                <TableRow key={idx}>
                  <TableCell>{atendente.name}</TableCell>
                  <TableCell align="right">{atendente.totalTickets}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(atendente.totalValor)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(
                      atendente.totalTickets > 0
                        ? atendente.totalValor / atendente.totalTickets
                        : 0
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </div>
  );
};

export default RelatorioVendas;
